//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { OpValidateInfo } from "../models/custom"
import { LogHelper } from "./log-helper"

import { Md5 } from "./md5-helper"
import { ICancellationToken } from './cancellation-token'

/**
 * SequentialItemSaver<TObject> is a helper class that allows sequential save of items in some collection.
 * This file contains the class itself and various related types.
 * */

// #region SequentialItemSaverStatus

/**
 * Item to save status.
 * */
export enum SequentialItemSaverStatus {
    /** Unknown status. */
    Unknown = 0,

    /** When save has succeded. */
    Succes = 1,

    /** A warning occured. */
    Warning = 2,

    /** An error occured. */
    Error = 3
}

// #endregion

// #region SequentialItemSaverProgress

/***
 * Type that contains information about saving progress.
 * */
export type SequentialItemSaverProgress<TObject> = {
    /** Index of an item. */
    index: number

    /** An item before save delegate call. */
    sourceItem: TObject

    /** An item returned by save delegate. */
    savedItem: TObject

    /** Save status. */
    status: SequentialItemSaverStatus,

    /** Error information. */
    error: any
}

// #endregion


//#region SequentialItemSaverResult

export class SequentialItemSaverResult<TObject> {

    //#region Properties

    public get hasErrors(): boolean {
        return _.some(this.progressItems, (progressItem: SequentialItemSaverProgress<TObject>) => progressItem.status === SequentialItemSaverStatus.Error)
    }

    public get errors(): any[] {
        return _
            .chain(this.progressItems)
            .filter((progressItem: SequentialItemSaverProgress<TObject>) => progressItem.status === SequentialItemSaverStatus.Error)
            .map((progressItem: SequentialItemSaverProgress<TObject>) => progressItem.error)
            .value()
    }

    public get savedItems(): TObject[] {
        return _
            .chain(this.progressItems)
            .filter((progressItem: SequentialItemSaverProgress<TObject>) => progressItem.status === SequentialItemSaverStatus.Succes)
            .map((progressItem: SequentialItemSaverProgress<TObject>) => progressItem.savedItem)
            .value()
    }

    //#endregion


    //#region Ctor

    constructor(public progressItems: SequentialItemSaverProgress<TObject>[]) { }

    //#endregion
}

//#endregion


// #region SequentialItemSaverOptions

/**
 * Save options.
 * */
export type SequentialItemSaverOptions<TObject> = {
    /** Collection of items to save. */
    itemsToSave: TObject[]

    /** Processing mode */
    mode?: SequentialItemSaverMode

    /** Delegate of save procedure. */
    saveDelegate: (toSave: TObject) => JQueryPromise<TObject>

    /**
     * Delegate of validation procedure.
     * Not used, for future implementation.
     * */
    validateDelegate?: (toValidate: TObject[]) => JQueryPromise<OpValidateInfo[]>

    /**
     * Item progress delegate, called after item has been processed.
     * */
    progressDelegate?: (progress: SequentialItemSaverProgress<TObject>) => void

    /**
     * Sequence finished delegate, called after all items has been processed.
     * */
    finishedDelegate?: (progress: SequentialItemSaverResult<TObject>) => void
}

// #endregion

// #region SequentialItemSaverState

/***
 * State of @see SequentialItemSave{T} class.
 * */
export enum SequentialItemSaverState {
    /** An unknown state. This state informs that object is in invalid state. */
    Unknown = 0,

    /** Indicates that saver is ready to start. */
    ReadyToStart = 1,

    /** Indicates that saver is currently running. */
    Running = 2,

    /** Indicates that save sequence has been cancelled. */
    Cancelled = 3,

    /** Indicates that saver has finished sequence. */
    Finished = 4
}

// #endregion


//#region SequentialItemSaverMode

export enum SequentialItemSaverMode {
    /** Continue processing despite errors */
    ContinueOnError = 0,

    /** Stop processing when an error occurrs */
    StopOnError = 1
}

//#endregion

// #region SequentialItemSaver

/**
 * Performs sequence of save operations on provided collection of items.
 * */
export class SequentialItemSaver<TObject> {
    // #region Consts

    private static LOG_PREFIX: string = '[SIMAX.Helpers.SequentialItemSaver]'

    /** Collection of states that indicate saver has finished its work. */
    private static get isDoneStates(): SequentialItemSaverState[] {
        return [
            SequentialItemSaverState.Cancelled,
            SequentialItemSaverState.Finished
        ]
    }

    // #endregion

    // #region Members

    /** If present can stop the saving process */
    private cancellationToken: ICancellationToken

    /** Promise that is resolved when saving ends */
    private resultDeferred: JQueryDeferred<SequentialItemSaverResult<TObject>>

    /** Collection of deferred objects for items to save. */
    private deferreds: JQueryDeferred<void>[]

    /** Collection of progress objects for items to save. */
    private itemsProgress: SequentialItemSaverProgress<TObject>[]

    /** Current @see SequentialItemSaver state. */
    private currentState: SequentialItemSaverState

    /** Provided options. */
    private options: SequentialItemSaverOptions<TObject>

    /** Saving sequence id. Used to distinguish from other operations and/or @see SequentialItemSaver objects. */
    private savingSequenceId: string

    // #endregion

    // #region Properties

    /** Current processing mode  */
    public get mode(): SequentialItemSaverMode {
        if (this.options != null && this.options.mode != null) {
            return this.options.mode
        }

        return SequentialItemSaverMode.ContinueOnError
    }

    /**  Indicates whether @see SequentialItemSaver is in `done` state. */
    public get isDone(): boolean { return SequentialItemSaver.isDoneStates.some((state: SequentialItemSaverState) => state == this.currentState) }

    /**  Indicates whether @see SequentialItemSaver is in `startable` (can be started) state. */
    public get isStartable(): boolean { return this.isReadyToStart || this.isDone }

    /**  Indicates whether @see SequentialItemSaver is in `ReadyToStarte` state. */
    public get isReadyToStart(): boolean { return this.currentState == SequentialItemSaverState.ReadyToStart }

    /**  Indicates whether @see SequentialItemSaver is in `Running` state. */
    public get isRunning(): boolean { return this.currentState == SequentialItemSaverState.Running }

    /**  Indicates whether @see SequentialItemSaver is in `Cancelled` state. */
    public get isCancelled(): boolean {
        return this.currentState === SequentialItemSaverState.Cancelled || (this.cancellationToken != null && this.cancellationToken.isCancelled())
    }

    /**  Indicates whether @see SequentialItemSaver is in `Finished` state. */
    public get isFinished(): boolean { return this.currentState == SequentialItemSaverState.Finished }

    /** Gets count of items to save. */
    public get itemsCount(): number { return this.options == null ? 0 : _.size(this.options.itemsToSave) }

    // #endregion

    /**
     * Intializes a new instance of the SequentialItemSaver class.
     * @param options SequentialItemSaver options.
     */
    public constructor(options: SequentialItemSaverOptions<TObject>) {
        this.options = options

        this.validateOptions()

        this.currentState = SequentialItemSaverState.ReadyToStart
    }

    // #region SequentialItemSaver

    /**
     * Starts saving sequence.
     */
    public start(cancellationToken: ICancellationToken = null): JQueryPromise<SequentialItemSaverResult<TObject>> {
        const canStart: boolean = this.isReadyToStart || this.isDone

        if (!canStart) {
            LogHelper.warn(SequentialItemSaver.LOG_PREFIX, `You can start until 'SequentialItemSaver' is ready to start or done.`)
            return
        }

        this.itemsProgress = []
        this.deferreds = []
        this.resultDeferred = $.Deferred()
        this.cancellationToken = cancellationToken

        this.currentState = SequentialItemSaverState.Running
        this.savingSequenceId = Md5.init(new Date())

        this.process()

        return this.resultDeferred.promise()
    }

    /**
     * Cancels saving sequence.
     */
    public cancel(): void {
        if (this.isRunning) {
            this.currentState = SequentialItemSaverState.Cancelled
        }
    }

    /**
     * Performs saving sequence.
     */
    private process(): void {
        LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Started save sequence processing`)

        if (this.isCancelled) {
            LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Sequence cancelled before any promise`)

            return
        }

        const deferred: JQueryDeferred<void> = $.Deferred<void>()
        let promise: JQueryPromise<void> = deferred.promise()

        if (_.size(this.options.itemsToSave) > 0) {
            this.options.itemsToSave.forEach((item: TObject, index: number) => {
                LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Processing item on index ${index}`)

                if (this.isCancelled) {
                    LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Sequence is cancelled, item on index ${index} will be marked with status ${SequentialItemSaverStatus.Unknown}`)

                    this.onCancelled(index, item)

                    return
                }

                const itemDeferred: JQueryDeferred<void> = $.Deferred<void>()

                promise = promise.then(() => {
                    LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Executing promise for item on index ${index}`)

                    if (this.isCancelled) {
                        LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Sequence is cancelled, item on index ${index} will be marked with status ${SequentialItemSaverStatus.Unknown} and empty promise will be returned`)

                        this.onCancelled(index, item)

                        return $.Deferred<void>().resolve().promise()
                    }

                    LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Executing save delegate for item on index ${index}`)

                    this.options.saveDelegate(item)
                        .then((result: TObject) => {
                            LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Save delegate for item on index ${index} succeeded`)

                            this.setItemProgress({
                                index: index,
                                sourceItem: item,
                                savedItem: result,
                                status: SequentialItemSaverStatus.Succes,
                                error: null
                            })

                            return result
                        })
                        .fail((error: any) => {
                            LogHelper.error(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Save delegate for item on index ${index} failed with error: '${error}'`)

                            this.setItemProgress({
                                index: index,
                                sourceItem: item,
                                savedItem: null,
                                status: SequentialItemSaverStatus.Error,
                                error: error
                            })
                        })
                        .always(() => {
                            LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Executing 'always' action for item on index ${index}`)

                            this.onProgress(this.getItemProgress(index))

                            itemDeferred.resolve()

                            this.onAllElementsDone()
                        })

                    return itemDeferred.promise()
                })

                this.deferreds.push(itemDeferred)
            })
        } else {
            this.resultDeferred.resolve(null)
        }

        LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] Resolving main deferrer promise, sequence will now start`)

        deferred.resolve()
    }

    /**
     * Sets item progres. If item already exists in collection, its replaced, otherwise added.
     */
    private setItemProgress(progress: SequentialItemSaverProgress<TObject>) {
        // JS extends the array on SET operation if index does not exists.
        // No checks required.
        this.itemsProgress[progress.index] = progress
    }

    /**
     * Returns progress item from specified index or new object if item does not exists in @see itemProgress collection.
     */
    private getItemProgress(index: number): SequentialItemSaverProgress<TObject> {
        return index < _.size(this.itemsProgress)
            ? this.itemsProgress[index]
            : {
                index: index,
                sourceItem: this.options.itemsToSave[index],
                savedItem: null,
                status: SequentialItemSaverStatus.Unknown,
                error: null
            }
    }

    /**
     * Checks if all items has been processe. If so sets proper state for `this` and calls @see onFinished method.
     */
    private onAllElementsDone(): void {
        const shouldStop: boolean = this.itemsProgress.some((item: SequentialItemSaverProgress<TObject>) => item.error != null)
            && this.mode === SequentialItemSaverMode.StopOnError

        const allFinished: boolean = this.itemsProgress != null
            && _.size(this.itemsProgress) == _.size(this.options.itemsToSave)
            && this.itemsProgress.every((item: SequentialItemSaverProgress<TObject>) => item != null)


        if (allFinished || shouldStop) {
            LogHelper.log(SequentialItemSaver.LOG_PREFIX, `[${this.savingSequenceId}] All elements done`)

            this.currentState = SequentialItemSaverState.Finished

            const result: SequentialItemSaverResult<TObject> = new SequentialItemSaverResult(this.itemsProgress)

            this.resultDeferred.resolve(result)
            this.onFinished(result)
        }
    }

    // #endregion

    // #region Setup

    /**
     * Validates provided options.
     */
    private validateOptions(): void {
        if (this.options == null) {
            throw new Error('You have to initialize options first')
        }

        // Collection of fields to validate.
        const requiredFields: string[] = [
            'itemsToSave',
            'saveDelegate'
        ]

        requiredFields.forEach((option: string) => this.validateOption(option))
    }

    /**
     * Validates if specific option has been provided. If not, throws an error.
     */
    private validateOption(name: string): void {
        if (this.options == null || this.options[name] == null) {
            throw new Error(`You have to provide ${name} option value`)
        }
    }

    // #endregion

    // #region Events

    /**
     * Calls finished delegate if one exists.
     */
    private onFinished(result: SequentialItemSaverResult<TObject>): void {
        if (this.options.finishedDelegate != null) {
            this.options.finishedDelegate(result)
        }
    }

    /**
     * Calls progress delegate if one exists.
     */
    private onProgress(progress: SequentialItemSaverProgress<TObject>): void {
        if (this.options.progressDelegate != null) {
            this.options.progressDelegate(progress)
        }
    }

    private onCancelled(index: number, sourceItem: TObject): void {
        this.setItemProgress({
            index: index,
            sourceItem: sourceItem,
            savedItem: null,
            status: SequentialItemSaverStatus.Unknown,
            error: null
        })

        this.onAllElementsDone()
    }

    // #endregion
}

// #endregion